import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4_fetch_mailbox_activity, v4_fetch_mailbox_comment, v4_fetch_mailbox_tag, v4_mailbox_comment, v4_mailbox_remove_tag, v4_mailbox_tag } from '../../actions/v4/mailBox';
import { v2_all_user } from '../../actions/v2/user';
import { v2_categories } from '../../actions/v2/masterData';
import { toast } from 'react-toastify';
import moment from 'moment';
import { formatLocalDateTime } from '../../dateTimeFormat';

const formatUserTag = (user) => {
    const userName = String(user?.FULL_NAME || '').trim();
    return `${user.USER_ID}::${userName || `User ${user.USER_ID}`}`;
};

const parseUserTag = (tagName) => {
    const value = String(tagName || '').trim();
    if (!value) return null;

    const separatorIndex = value.indexOf('::');
    if (separatorIndex === -1) {
        return { id: value, name: value, rawTagName: value };
    }

    const id = value.substring(0, separatorIndex).trim();
    const name = value.substring(separatorIndex + 2).trim();
    return {
        id: id || value,
        name: name || id || value,
        rawTagName: value,
    };
};

const hashString = (value = '') => {
    const text = String(value || '');
    let hash = 0;

    for (let i = 0; i < text.length; i += 1) {
        hash = ((hash << 5) - hash) + text.charCodeAt(i);
        hash |= 0;
    }

    return Math.abs(hash);
};

const getTagBadgeStyle = (value, type) => {
    const hash = hashString(`${type}:${String(value || '').toLowerCase()}`);
    const hue = hash % 360;

    if (type === 'category') {
        return {
            // Pastel category badges with full hue spread
            backgroundColor: `hsl(${hue} 90% 92%)`,
            color: `hsl(${hue} 65% 26%)`,
            border: `1px solid hsl(${hue} 65% 66%)`,
        };
    }

    return {
        // Strong user badges with full hue spread
        backgroundColor: `hsl(${hue} 72% 42%)`,
        color: '#ffffff',
        border: `1px solid hsl(${hue} 72% 30%)`,
    };
};


const ReadMailRightWidget = ({ messageId, mailboxId, folder, emailMetadata = {} }) => {
    const [activeTab, setActiveTab] = useState('tag');

    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [activityLogs, setActivityLogs] = useState([])
    const [categoryTags, setCategoryTags] = useState([]);
    const [privateTags, setPrivateTags] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [selectedCategoryTags, setSelectedCategoryTags] = useState([]);

    const [newPrivateTag, setNewPrivateTag] = useState('');
    const [editingPrivateTagName, setEditingPrivateTagName] = useState('');
    const [editingPrivateTagValue, setEditingPrivateTagValue] = useState('');

    const filteredUsers = useMemo(() => {
        const query = userSearch.trim().toLowerCase();
        if (!query) return allUsers;

        return allUsers.filter((user) =>
            String(user.FULL_NAME || '')
                .toLowerCase()
                .includes(query)
        );
    }, [allUsers, userSearch]);

    const getTagPayload = (payload = {}) => ({
        ...payload,
        id: mailboxId,
        folder,
        emailSubject: String(emailMetadata?.subject || '').trim(),
        emailFromName: String(emailMetadata?.fromName || '').trim(),
        emailFromAddress: String(emailMetadata?.fromAddress || '').trim(),
    });

    const getCommentPayload = (payload = {}) => ({
        ...payload,
        id: mailboxId,
        folder,
        emailSubject: String(emailMetadata?.subject || '').trim(),
        emailFromName: String(emailMetadata?.fromName || '').trim(),
        emailFromAddress: String(emailMetadata?.fromAddress || '').trim(),
        emailTo: Array.isArray(emailMetadata?.to) ? emailMetadata.to : [],
        emailCc: Array.isArray(emailMetadata?.cc) ? emailMetadata.cc : [],
    });

    const handleAddPrivateTag = async () => {
        const normalizedTagName = String(newPrivateTag || '').trim();
        if (!normalizedTagName) return;

        const duplicate = privateTags.some(
            (tag) => String(tag || '').trim().toLowerCase() === normalizedTagName.toLowerCase()
        );
        if (duplicate) {
            toast.info('Private tag already exists.');
            return;
        }

        try {
            const response = await v4_mailbox_tag({
            messageId,
            tagName: normalizedTagName,
            tagType: 'private',
            ...getTagPayload(),
            });

            const payload = response?.result || {};
            if (response?.status && payload?.status) {
                toast.success(payload?.message || 'Private tag added.');
                setNewPrivateTag('');
                await Promise.all([loadTags(), fetchActivity()]);
                return;
            }

            if (response?.status && payload?.status === false) {
                toast.info(payload?.message || 'Private tag already exists.');
                return;
            }

            toast.error(response?.error?.message || payload?.message || 'Failed to add private tag.');
        } catch (error) {
            console.error('Private tag add error:', error);
            toast.error('Something went wrong while adding private tag.');
        }
    };

    const resetPrivateTagEditor = () => {
        setEditingPrivateTagName('');
        setEditingPrivateTagValue('');
    };

    const handleStartEditPrivateTag = (tagName) => {
        const normalizedTagName = String(tagName || '').trim();
        if (!normalizedTagName) {
            toast.error('Invalid private tag.');
            return;
        }

        setEditingPrivateTagName(normalizedTagName);
        setEditingPrivateTagValue(normalizedTagName);
    };

    const handleRemovePrivateTag = async (tagName) => {
        const normalizedTagName = String(tagName || '').trim();
        if (!normalizedTagName) {
            toast.error('Invalid private tag.');
            return;
        }

        try {
            const response = await v4_mailbox_remove_tag({
                id: mailboxId,
                folder,
                messageId,
                tagName: normalizedTagName,
                tagType: 'private',
            });

            const payload = response?.result || {};
            if (response?.status && payload?.status) {
                toast.success(payload?.message || 'Private tag removed.');

                if (editingPrivateTagName.toLowerCase() === normalizedTagName.toLowerCase()) {
                    resetPrivateTagEditor();
                }

                await Promise.all([loadTags(), fetchActivity()]);
                return;
            }

            toast.info(payload?.message || 'Private tag not found.');
        } catch (error) {
            console.error('Private tag remove error:', error);
            toast.error('Something went wrong while removing private tag.');
        }
    };

    const handleSavePrivateTagEdit = async () => {
        const originalTagName = String(editingPrivateTagName || '').trim();
        const updatedTagName = String(editingPrivateTagValue || '').trim();

        if (!originalTagName) {
            resetPrivateTagEditor();
            return;
        }

        if (!updatedTagName) {
            toast.error('Private tag cannot be empty.');
            return;
        }

        if (updatedTagName.toLowerCase() === originalTagName.toLowerCase()) {
            toast.info('No changes to save.');
            resetPrivateTagEditor();
            return;
        }

        const duplicate = privateTags.some((tag) => {
            const normalized = String(tag || '').trim().toLowerCase();
            return normalized === updatedTagName.toLowerCase() && normalized !== originalTagName.toLowerCase();
        });

        if (duplicate) {
            toast.info('Private tag already exists.');
            return;
        }

        try {
            const removeResponse = await v4_mailbox_remove_tag({
                id: mailboxId,
                folder,
                messageId,
                tagName: originalTagName,
                tagType: 'private',
            });
            const removePayload = removeResponse?.result || {};

            if (!(removeResponse?.status && removePayload?.status)) {
                toast.info(removePayload?.message || 'Private tag not found.');
                return;
            }

            const addResponse = await v4_mailbox_tag({
                messageId,
                tagName: updatedTagName,
                tagType: 'private',
                ...getTagPayload(),
            });
            const addPayload = addResponse?.result || {};

            if (addResponse?.status && addPayload?.status) {
                toast.success('Private tag updated successfully.');
                resetPrivateTagEditor();
                await Promise.all([loadTags(), fetchActivity()]);
                return;
            }

            try {
                await v4_mailbox_tag({
                    messageId,
                    tagName: originalTagName,
                    tagType: 'private',
                    ...getTagPayload(),
                });
            } catch (rollbackError) {
                console.error('Private tag rollback error:', rollbackError);
            }

            toast.error(addResponse?.error?.message || addPayload?.message || 'Failed to update private tag.');
        } catch (error) {
            console.error('Private tag edit error:', error);
            toast.error('Something went wrong while editing private tag.');
        }
    };

    const handleToggleUserSelection = (userId) => {
        const normalizedId = String(userId);
        setSelectedUserIds((prev) => (
            prev.includes(normalizedId)
                ? prev.filter((id) => id !== normalizedId)
                : [...prev, normalizedId]
        ));
    };

    const loadTags = useCallback(async () => {
        try {
            const tagRes = await v4_fetch_mailbox_tag(messageId, mailboxId, folder);
            if (tagRes?.status) {
                const tags = tagRes.result.data || [];
                const normalizedUsers = [];
                const seenUsers = new Set();

                tags
                    .filter((tag) => tag.TAG_TYPE === 'user' || tag.TAG_TYPE === 'asset')
                    .forEach((tag) => {
                        const parsed = parseUserTag(tag.TAG_NAME);
                        if (!parsed) return;

                        const key = String(parsed.id || parsed.name).toLowerCase();
                        if (!seenUsers.has(key)) {
                            seenUsers.add(key);
                            normalizedUsers.push(parsed);
                        }
                    });

                setAssignedUsers(normalizedUsers);
                setCategoryTags(tags.filter(tag => tag.TAG_TYPE === 'category').map(t => t.TAG_NAME));
                setPrivateTags(tags.filter(tag => tag.TAG_TYPE === 'private').map(t => t.TAG_NAME));
            }
        } catch (err) {
            console.error('Failed to fetch tags:', err);
        }
    }, [messageId, mailboxId, folder]);

    const handleAssignUsers = async () => {
        if (!selectedUserIds.length) return;

        const assignedUserIdSet = new Set(assignedUsers.map((user) => String(user.id)));
        const usersToAssign = allUsers.filter((user) =>
            selectedUserIds.includes(String(user.USER_ID)) &&
            !assignedUserIdSet.has(String(user.USER_ID))
        );

        if (!usersToAssign.length) {
            toast.info('Selected users are already assigned.');
            setSelectedUserIds([]);
            return;
        }

        try {
            const response = await v4_mailbox_tag({
                messageId,
                tagName: usersToAssign.map((user) => formatUserTag(user)),
                tagType: 'user',
                ...getTagPayload(),
            });

            const payload = response?.result || {};
            if (response?.status && payload?.status) {
                const successCount = Number(payload.insertedCount) || usersToAssign.length;
                const skippedCount = Number(payload.skippedCount) || 0;

                toast.success(
                    `${successCount} user${successCount > 1 ? 's' : ''} assigned successfully.`
                );

                if (skippedCount > 0) {
                    toast.info(
                        `${skippedCount} user${skippedCount > 1 ? 's were' : ' was'} already tagged.`
                    );
                }

                await Promise.all([loadTags(), fetchActivity()]);
                setSelectedUserIds([]);
                return;
            }

            if (response?.status && payload?.status === false) {
                toast.info(payload?.message || 'Selected users are already assigned.');
                setSelectedUserIds([]);
                return;
            }

            toast.error('Failed to assign users.');
        } catch (error) {
            console.error('User assignment error:', error);
            toast.error('Something went wrong while assigning users.');
        }
    };

    const handleRemoveAssignedUser = async (userTag) => {
        const tagNameToRemove = String(userTag?.rawTagName || '').trim();
        if (!tagNameToRemove) {
            toast.error('Invalid user tag.');
            return;
        }

        try {
            const response = await v4_mailbox_remove_tag({
                id: mailboxId,
                folder,
                messageId,
                tagName: tagNameToRemove,
                tagType: 'user',
            });

            const payload = response?.result || {};
            if (response?.status && payload?.status) {
                toast.success(payload?.message || 'Assigned user removed.');
                await Promise.all([loadTags(), fetchActivity()]);
                return;
            }

            toast.info(payload?.message || 'User tag not found.');
        } catch (error) {
            console.error('User tag remove error:', error);
            toast.error('Something went wrong while removing assigned user.');
        }
    };

    const handleRemoveCategoryTag = async (tagName) => {
        const normalizedTagName = String(tagName || '').trim();
        if (!normalizedTagName) {
            toast.error('Invalid category tag.');
            return;
        }

        try {
            const response = await v4_mailbox_remove_tag({
                id: mailboxId,
                folder,
                messageId,
                tagName: normalizedTagName,
                tagType: 'category',
            });

            const payload = response?.result || {};
            if (response?.status && payload?.status) {
                toast.success(payload?.message || 'Category tag removed.');
                await Promise.all([loadTags(), fetchActivity()]);
                return;
            }

            toast.info(payload?.message || 'Category tag not found.');
        } catch (error) {
            console.error('Category tag remove error:', error);
            toast.error('Something went wrong while removing category tag.');
        }
    };

    const handleToggleCategorySelection = (categoryName) => {
        const normalized = String(categoryName || '').trim();
        if (!normalized) return;

        setSelectedCategoryTags((prev) => (
            prev.includes(normalized)
                ? prev.filter((item) => item !== normalized)
                : [...prev, normalized]
        ));
    };

    const handleAddSelectedCategories = async () => {
        if (!selectedCategoryTags.length) return;

        const existingCategoryTagSet = new Set(
            categoryTags.map((tag) => String(tag || '').trim().toLowerCase())
        );

        const tagsToAdd = selectedCategoryTags.filter(
            (tag) => !existingCategoryTagSet.has(String(tag).trim().toLowerCase())
        );

        if (!tagsToAdd.length) {
            toast.info('Selected categories are already tagged.');
            setSelectedCategoryTags([]);
            return;
        }

        try {
            const responses = await Promise.allSettled(
                tagsToAdd.map((tag) => v4_mailbox_tag({
                    messageId,
                    tagName: tag,
                    tagType: 'category',
                    ...getTagPayload(),
                }))
            );

            let successCount = 0;
            let failCount = 0;

            responses.forEach((response) => {
                if (response.status === 'fulfilled' && response.value?.status) {
                    successCount += 1;
                } else {
                    failCount += 1;
                }
            });

            if (successCount > 0) {
                toast.success(
                    `${successCount} categor${successCount > 1 ? 'ies' : 'y'} added successfully.`
                );
                await Promise.all([loadTags(), fetchActivity()]);
            }

            if (failCount > 0) {
                toast.error(`Failed to add ${failCount} categor${failCount > 1 ? 'ies' : 'y'}.`);
            }

            setSelectedCategoryTags([]);
        } catch (error) {
            console.error('Category tag add error:', error);
            toast.error('Something went wrong while adding categories.');
        }
    };

    const handleAddComment = () => {
        if (!newComment.trim()) return;

        v4_mailbox_comment(getCommentPayload({ messageId, comment: newComment }))
            .then(async (res) => {
                if (res?.status) {
                    try {
                        const fetchRes = await v4_fetch_mailbox_comment(messageId, mailboxId, folder);
                        if (fetchRes?.status) {
                            setComments(fetchRes.result.data || []);
                        }

                        setNewComment('');
                        toast.success('Comment added successfully');
                    } catch (fetchErr) {
                        console.error('Fetch comments error:', fetchErr);
                        toast.warn('Comment added, but failed to refresh comments');
                    }
                } else {
                    toast.error(res?.message || 'Failed to add comment');
                }
            })
            .catch((err) => {
                console.error('Comment add error:', err);
                toast.error('Something went wrong while adding comment');
            });
    };

    const fetchActivity = useCallback(async () => {
        try {
            const res = await v4_fetch_mailbox_activity(messageId, mailboxId, folder);
            if (res?.status) {
                setActivityLogs(res.result.data || []);
            } else {
                toast.error(res?.error.message || 'Failed to load activity logs');
            }
        } catch (err) {
            console.error('Activity fetch error:', err);
            toast.error('Something went wrong while fetching activity logs');
        }
    }, [messageId, mailboxId, folder]);

    useEffect(() => {
        const loadUsers = async () => {
            try {
                const usersRes = await v2_all_user();
                if (usersRes?.status) {
                    setAllUsers(usersRes.result || []);
                }
            } catch (error) {
                console.error('Failed to fetch users:', error);
            }
        };

        loadUsers();
    }, []);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const res = await v2_categories();
                if (res?.status) {
                    setCategoryOptions(res.result || []);
                }
            } catch (error) {
                console.error('Failed to fetch categories:', error);
            }
        };

        loadCategories();
    }, []);

    useEffect(() => {
        if (!messageId) return;
        setActiveTab('tag');
        setSelectedUserIds([]);
        setUserSearch('');
        setSelectedCategoryTags([]);
        setNewPrivateTag('');
        setEditingPrivateTagName('');
        setEditingPrivateTagValue('');

        const fetchData = async () => {
            // Fetch activity
            await fetchActivity();

            // Fetch comments
            try {
                const fetchRes = await v4_fetch_mailbox_comment(messageId, mailboxId, folder);
                if (fetchRes?.status) {
                    setComments(fetchRes.result.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch comments:', err);
            }

            // Fetch tags
            await loadTags();
        };

        fetchData();
    }, [messageId, mailboxId, folder, fetchActivity, loadTags]);

    const getInitials = (name = '') => {
        if (!name) return '';
        return name
            .trim()
            .split(/\s+/)
            .slice(0, 2)              // first + last name
            .map(word => word[0].toUpperCase())
            .join('');
    };


    return (
        <div className="col-md-4">
            <div className="widget bg-white" style={{ marginRight: '-90px' }}>
                <div className="widget-content">
                    {/* Tabs */}
                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'activity' ? 'active' : ''}`}
                                onClick={() => setActiveTab('activity')}
                            >
                                Activity
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'comment' ? 'active' : ''}`}
                                onClick={() => setActiveTab('comment')}
                            >
                                Comments
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'tag' ? 'active' : ''}`}
                                onClick={() => setActiveTab('tag')}
                            >
                                Tags
                            </button>
                        </li>
                    </ul>

                    {/* Tab content */}
                    <div className="column-wrap">
                        <div className="coloumn">
                            {activeTab === 'activity' && (
                                <div className="activity">
                                    <div className="activity-items">
                                        {activityLogs && activityLogs.length > 0 ? (
                                            activityLogs.map((log, index) => (
                                                <div key={index} className="activity-item-wrap activity-call mb-3">
                                                    <div className="activity-item-badge">
                                                        <i className="aroicon-action-send-message" />
                                                    </div>
                                                    <div className="activity-item">
                                                        <div className="activity-item-meta">
                                                            <div className="activity-user">
                                                                {getInitials(log.USER_NAME || `User ${log.USER_ID}`)}
                                                            </div>
                                                            <p className="activity-summary">
                                                                <strong>{log.USER_NAME || 'User'}</strong> {log.ACTIVITY}
                                                            </p>
                                                            <p className="activity-timestamp">
                                                                {formatLocalDateTime(log.CREATED_ON)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted">No activity found.</p>
                                        )}
                                    </div>
                                </div>
                            )}


                            {activeTab === 'comment' && (
                                <div className="comments">
                                    <h5 className="mb-3">Comments</h5>

                                    {/* Show existing comments if any */}
                                    {comments && comments.length > 0 ? (
                                        <ul className="list-unstyled">
                                            {comments.map((c, index) => (
                                                <li key={index} className="mb-3 border-bottom pb-2">
                                                    <div className="fw-semibold">{c.USER_NAME || 'User'}</div>
                                                    <div>{c.COMMENT}</div>
                                                    <small className="text-muted"> {moment(c.CREATED_ON).format('DD MMM YYYY, hh:mm A')}</small>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-muted">No comments yet.</p>
                                    )}

                                    {/* Always show Add Comment section */}
                                    <div className="mt-3">
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            placeholder="Add a comment..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                        />
                                        <button
                                            className="btn mt-2"
                                            onClick={handleAddComment}
                                            style={{
                                                backgroundColor: 'rgb(104, 149, 181)',
                                                color: 'white'
                                            }}
                                            disabled={!newComment.trim()}
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            )}


                            {activeTab === 'tag' && (
                                <div className="tags">
                                    <h5 className="mb-3">Tags</h5>

                                    {/* Users */}
                                    <div className="mb-4">
                                        <h6 className="fw-semibold">Users</h6>
                                        <div className="d-flex flex-wrap gap-2 mb-2">
                                            {assignedUsers.length > 0 ? assignedUsers.map((user, idx) => (
                                                <span
                                                    key={`${user.id}-${idx}`}
                                                    className="badge d-inline-flex align-items-center"
                                                    style={getTagBadgeStyle(`${user.id}:${user.name}`, 'user')}
                                                >
                                                    {user.name}
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 ms-2"
                                                        style={{
                                                            textDecoration: 'none',
                                                            lineHeight: 1,
                                                            color: 'inherit',
                                                        }}
                                                        title="Remove user tag"
                                                        onClick={() => handleRemoveAssignedUser(user)}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            )) : (
                                                <small className="text-muted">No users assigned.</small>
                                            )}
                                        </div>
                                        <div className="mb-2">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Search users..."
                                                value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                            />
                                        </div>
                                        <div className="border rounded p-2 mb-2" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                                            {filteredUsers.length > 0 ? filteredUsers.map((user) => (
                                                <div className="form-check" key={user.USER_ID}>
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`assign-user-${user.USER_ID}`}
                                                        checked={selectedUserIds.includes(String(user.USER_ID))}
                                                        onChange={() => handleToggleUserSelection(user.USER_ID)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`assign-user-${user.USER_ID}`}>
                                                        {user.FULL_NAME}
                                                        {user.ROLE_NAME ? ` (${user.ROLE_NAME})` : ''}
                                                    </label>
                                                </div>
                                            )) : (
                                                <small className="text-muted">No users found.</small>
                                            )}
                                        </div>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={handleAssignUsers}
                                            disabled={selectedUserIds.length === 0}
                                        >
                                            Assign selected users
                                        </button>
                                    </div>

                                    <hr className="my-4" />

                                    {/* Category Tags */}
                                    <div className="mb-4">
                                        <h6 className="fw-semibold">Category Tags</h6>
                                        <div className="d-flex flex-wrap gap-2 mb-2">
                                            {categoryTags.map((tag, idx) => (
                                                <span
                                                    key={`${tag}-${idx}`}
                                                    className="badge d-inline-flex align-items-center"
                                                    style={getTagBadgeStyle(tag, 'category')}
                                                >
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 ms-2"
                                                        style={{
                                                            textDecoration: 'none',
                                                            lineHeight: 1,
                                                            color: 'inherit',
                                                        }}
                                                        title="Remove category tag"
                                                        onClick={() => handleRemoveCategoryTag(tag)}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                        <div className="border rounded p-2 mb-2" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                                            {categoryOptions.length > 0 ? categoryOptions.map((item) => (
                                                <div className="form-check" key={item.ID}>
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`category-tag-${item.ID}`}
                                                        checked={selectedCategoryTags.includes(item.CATEGORY_NAME)}
                                                        onChange={() => handleToggleCategorySelection(item.CATEGORY_NAME)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`category-tag-${item.ID}`}>
                                                        {item.CATEGORY_NAME}
                                                    </label>
                                                </div>
                                            )) : (
                                                <small className="text-muted">No categories found.</small>
                                            )}
                                        </div>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={handleAddSelectedCategories}
                                            disabled={selectedCategoryTags.length === 0}
                                        >
                                            Add selected categories
                                        </button>
                                    </div>

                                    <hr className="my-4" />

                                    {/* Private Tags */}
                                    <div className="mb-4">
                                        <h6 className="fw-semibold">Private Tags</h6>
                                        <div className="d-flex flex-wrap gap-2 mb-2">
                                            {privateTags.length > 0 ? privateTags.map((tag, idx) => (
                                                <span
                                                    key={`${tag}-${idx}`}
                                                    className="badge bg-warning text-dark d-inline-flex align-items-center"
                                                >
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 ms-2"
                                                        style={{
                                                            textDecoration: 'none',
                                                            lineHeight: 1,
                                                            color: 'inherit',
                                                            fontSize: '11px',
                                                        }}
                                                        title="Edit private tag"
                                                        onClick={() => handleStartEditPrivateTag(tag)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 ms-2"
                                                        style={{
                                                            textDecoration: 'none',
                                                            lineHeight: 1,
                                                            color: 'inherit',
                                                        }}
                                                        title="Remove private tag"
                                                        onClick={() => handleRemovePrivateTag(tag)}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            )) : (
                                                <small className="text-muted">No private tags yet.</small>
                                            )}
                                        </div>

                                        {editingPrivateTagName && (
                                            <div className="d-flex gap-2 mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Edit private tag"
                                                    value={editingPrivateTagValue}
                                                    onChange={(e) => setEditingPrivateTagValue(e.target.value)}
                                                />
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={handleSavePrivateTagEdit}
                                                    disabled={!editingPrivateTagValue.trim()}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={resetPrivateTagEditor}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}

                                        <div className="d-flex gap-2">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Add private tag"
                                                value={newPrivateTag}
                                                onChange={(e) => setNewPrivateTag(e.target.value)}
                                            />
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={handleAddPrivateTag}
                                                disabled={!newPrivateTag.trim()}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}


                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReadMailRightWidget;
